import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { Container } from "reactstrap";
import ActionMenu from "../../../components/Common/ActionMenu";
import { get, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";

/* -------------------------------------------------------------------------
 * AdminFAQsPage — full "FAQs" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 * ---------------------------------------------------------------------- */

const AdminFAQsPage = ({ onFilterClick, onExportClick }) => {
  document.title = "FAQs | Omaha Food Lovers";
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const columns = [
    { key: "question", label: "Question", width: "2fr" },
    { key: "answer", label: "Answer", width: "2fr" },
    { key: "category", label: "Category", width: "1fr" },
    { key: "sortOrder", label: "Sort Order", width: "0.8fr" },
    { key: "isActive", label: "Status", width: "0.8fr" },
    { key: "createdAt", label: "Created At", width: "1fr" },
    {
      key: "actions",
      label: "",
      width: "60px",
      align: "right",
      className: "rp-table__cell--actions",
      render: (row) => (
        <ActionMenu
          isOpen={activeDropdown === row.id}
          onToggle={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
          items={[
            {
              label: "Edit",
              icon: "edit",
              onClick: () => navigate(`/admin/faqs/${row.id}/edit`, { state: { faqData: row } }),
            },
            {
              label: "Delete",
              icon: "delete",
              onClick: () => handleDelete(row.id),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const transformFAQData = (faqArray) =>
    faqArray.map((faqItem) => ({
      id: faqItem.id,
      question: faqItem.question || "N/A",
      answer: faqItem.answer || "N/A",
      category: faqItem.category || "N/A",
      sortOrder: faqItem.sort_order || 0,
      isActive: faqItem.is_active ? "Active" : "Inactive",
      createdAt: faqItem.created_at ? new Date(faqItem.created_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : "N/A",
    }));

  const fetchFAQs = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminGlobalFAQs}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const data = response.data.data;
        const newFAQs = data.data || [];
        const transformedFAQs = transformFAQData(newFAQs);

        if (isLoadMore) {
          setFaqs((prev) => [...prev, ...transformedFAQs]);
        } else {
          setFaqs(transformedFAQs);
        }

        setHasMore(newFAQs.length === 20);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch FAQs:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchFAQs(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleDelete = (faqId) => {
    setFaqToDelete(faqId);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const confirmDelete = async () => {
    if (!faqToDelete) return;

    try {
      const endpoint = endpoints.adminGlobalFAQ.replace("{{global_faq_id}}", faqToDelete);
      await del(endpoint);
      toastr.success("FAQ deleted successfully");
      setShowDeleteModal(false);
      setFaqToDelete(null);
      fetchFAQs(1, false);
    } catch (error) {
      console.error("Failed to delete FAQ:", error);
      toastr.error("Failed to delete FAQ");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setFaqToDelete(null);
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "sort_order",
      options: [
        { value: "sort_order", label: "Sort Order" },
        { value: "created_at", label: "Created At" },
        { value: "question", label: "Question" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "asc",
    },
  ];

  useEffect(() => {
    fetchFAQs(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 className="rp-card__title" style={{ margin: 0 }}>FAQs</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of frequently asked questions and answers.</p>
            </div>
            <SecondaryButton
              variant="orange"
              icon={tickIcon}
              onClick={() => navigate("/admin/faqs/create")}
            >
              Add FAQ
            </SecondaryButton>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by question or category"
            filters={filters}
          />

          <DataTable
            columns={columns}
            rows={faqs}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete FAQ"
        message="Are you sure you want to delete this FAQ? This action cannot be undone."
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        itemName="FAQ"
      />
    </div>
  );
};

AdminFAQsPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminFAQsPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminFAQsPage;
